import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, X, Printer, FilePdf, PencilSimple, Trash, ArrowsClockwise, WarningCircle, Lock } from "@phosphor-icons/react";
import InvoicePrintable from "./InvoicePrintable";
import InvoiceDocument, { fmtHrDate } from "./invoice/InvoiceDocument";
import InvoiceItemsEditor, { calcTotals } from "./invoice/InvoiceItemsEditor";

/**
 * Create / prepare / issue an invoice for one reservation.
 *   draft  → PRIPREMI (save) + ZAKLJUČI (lock & number)
 *   issued → read-only + ISPIS + PDF + STORNIRAJ
 */
export default function InvoiceModal({ invoiceId, reservationId, invoiceType = "racun", onClose, onSaved }) {
  const [inv, setInv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [mode, setMode] = useState("edit");
  const initRan = useRef(false); // StrictMode guard against double POST

  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    (async () => {
      try {
        if (invoiceId) {
          const { data } = await api.get(`/invoices/${invoiceId}`);
          setInv(data);
          setMode("preview");
        } else {
          const { data } = await api.post("/invoices", { invoice_type: invoiceType, reservation_id: reservationId });
          setInv(data);
          setMode(data.status === "issued" ? "preview" : "edit");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Greška pri učitavanju");
        onClose?.();
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isIssued = inv?.status === "issued";
  const editable = !isIssued && mode === "edit";
  const itemsLocked = (inv?.items || []).some((i) => i.locked);
  const view = useMemo(() => (inv ? { ...inv, ...(itemsLocked ? {} : calcTotals(inv.items || [])) } : null), [inv, itemsLocked]);
  const update = (patch) => setInv((prev) => ({ ...prev, ...patch }));

  const buildPayload = () => {
    const keys = ["due_date", "valid_until", "payment_type", "card_type", "notes", "legal_text", "client_name", "client_address",
      "client_postal_code", "client_city", "client_country", "client_email", "client_vat_id", "client_reference_code"];
    const p = Object.fromEntries(keys.filter((k) => inv[k] !== undefined && inv[k] !== null).map((k) => [k, inv[k]]));
    if (!itemsLocked) p.items = inv.items;
    return p;
  };
  const flash = (text) => toast.success(text);

  const savePripremi = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, buildPayload());
      setInv(data); setMode("preview"); flash("Račun je spremljen"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška pri spremanju"); }
    finally { setSaving(false); }
  };

  const zakljuci = async () => {
    if (!(inv.legal_text || "").trim() && inv.invoice_type !== "ponuda") { toast.error("Tekst o PDV-u je obavezan."); setMode("edit"); return; }
    if (!window.confirm("Zaključivanjem će račun dobiti trajni broj i više se neće moći uređivati. Nastaviti?")) return;
    setIssuing(true);
    try {
      if (mode === "edit") await api.put(`/invoices/${inv.id}`, buildPayload());
      const { data } = await api.post(`/invoices/${inv.id}/issue`);
      setInv(data); setMode("preview"); flash(`Račun ${data.number} je zaključen`); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška pri zaključivanju"); }
    finally { setIssuing(false); }
  };

  const refresh = async () => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/refresh`);
      setInv(data); flash("Podaci osvježeni iz rezervacije");
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };

  const togglePaid = async () => {
    try { const { data } = await api.put(`/invoices/${inv.id}`, { paid: !inv.paid }); setInv(data); onSaved?.(); }
    catch { toast.error("Greška"); }
  };

  const togglePaidDraft = async (paid) => {
    try {
      await api.put(`/reservations/${inv.reservation_id}`, { payment_status: paid ? "paid" : "unpaid" });
      setInv((prev) => ({ ...prev, paid, show_payment_info: !paid }));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };

  const storno = async () => {
    if (!window.confirm(`Storno računa ${inv.number}? Bit će kreiran novi storno račun s trajnim brojem.`)) return;
    try {
      const { data } = await api.post("/invoices", { invoice_type: "storno", reservation_id: inv.reservation_id, storno_of_invoice_id: inv.id });
      toast.success(`Storniran • ${data.number}`); onSaved?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška pri storniranju"); }
  };

  const deleteDraft = async () => {
    if (!window.confirm("Obrisati ovaj nacrt računa? Ova akcija se ne može poništiti.")) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Nacrt je obrisan"); onSaved?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška pri brisanju"); }
  };

  const pdfFilename = () => {
    const t = { racun: "Racun", avans: "Predujam", ponuda: "Ponuda", storno: "Storno" }[inv?.invoice_type] || "Racun";
    return `${t}-${String(inv?.number || "nacrt").replace(/^br\.?\s*/i, "").replace(/[/\\?%*:|"<>\s]/g, "-")}.pdf`;
  };
  const doPrint = () => {
    const prev = document.title;
    document.title = pdfFilename().replace(/\.pdf$/, "");
    window.requestAnimationFrame(() => { window.print(); setTimeout(() => { document.title = prev; }, 500); });
  };
  const doPDF = async () => {
    const el = document.querySelector(".invoice-print-root");
    if (!el) return toast.error("Ne mogu pronaći dokument za PDF");
    const prev = { left: el.style.left, position: el.style.position };
    el.style.left = "0px"; el.style.position = "static";
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 0, filename: pdfFilename(), image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }).from(el).save();
    } catch { toast.error("Greška pri kreiranju PDF-a"); }
    finally { el.style.left = prev.left; el.style.position = prev.position; }
  };

  if (!view) {
    return <ModalShell onClose={onClose}><div className="flex h-96 items-center justify-center"><CircleNotch size={26} className="animate-spin text-primary" /></div></ModalShell>;
  }

  return (
    <>
      <ModalShell onClose={onClose}>
        <ModalHeader inv={inv} mode={mode} isIssued={isIssued} onEdit={() => setMode("edit")} onRefresh={refresh} onClose={onClose} />
        <div className="max-h-[calc(92dvh-140px)] overflow-y-auto bg-slate-100 px-4 py-5 sm:px-8">
          <Banners inv={inv} editable={editable} itemsLocked={itemsLocked} />
          {!isIssued && inv.reservation_id && inv.invoice_type !== "ponuda" && <PaidSwitch paid={!!inv.paid} onToggle={togglePaidDraft} />}
          {editable && !itemsLocked && <div className="mb-3"><InvoiceItemsEditor items={inv.items || []} onChange={(items) => update({ items })} /></div>}
          <div className="mx-auto max-w-[860px] rounded-md bg-white p-6 shadow-sm sm:p-10">
            <InvoiceDocument inv={view} editable={editable} onChange={update} />
          </div>
        </div>
        <ModalFooter
          editable={editable} isIssued={isIssued} inv={inv} saving={saving} issuing={issuing}
          onSave={savePripremi} onIssue={zakljuci} onDelete={deleteDraft} onPrint={doPrint} onPDF={doPDF}
          onTogglePaid={togglePaid} onStorno={storno} onClose={onClose}
        />
      </ModalShell>
      {createPortal(<InvoicePrintable inv={view} />, document.body)}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          body > *:not(.invoice-print-root) { display: none !important; }
          .invoice-print-root { position: static !important; left: 0 !important; width: 100% !important; }
        }
      `}</style>
    </>
  );
}

const TYPE_TITLES = { racun: "Račun", avans: "Račun za predujam", ponuda: "Ponuda", storno: "Storno račun" };

function ModalHeader({ inv, mode, isIssued, onEdit, onRefresh, onClose }) {
  const btn = "flex items-center gap-1 rounded-md bg-white/15 px-3 py-1 text-xs font-semibold text-white hover:bg-white/25";
  return (
    <div className="flex items-center justify-between bg-[#0f3d4c] px-6 py-3.5">
      <h2 className="text-lg font-semibold text-white" data-testid="invoice-modal-title">
        {TYPE_TITLES[inv.invoice_type] || "Račun"} {isIssued ? inv.number : <span className="text-sm font-normal text-amber-300">· nacrt{inv.auto_generated ? " (automatski)" : ""}</span>}
      </h2>
      <div className="flex items-center gap-2">
        {!isIssued && inv.reservation_id && <button onClick={onRefresh} className={btn} data-testid="inv-btn-refresh" title="Ponovno učitaj cijenu, gosta i izdavatelja iz rezervacije"><ArrowsClockwise size={13} weight="bold" /> Osvježi</button>}
        {mode === "preview" && !isIssued && <button onClick={onEdit} className={btn} data-testid="inv-btn-edit"><PencilSimple size={13} weight="bold" /> Uredi</button>}
        <button onClick={onClose} className="rounded-full p-1 text-white/90 hover:bg-white/20" data-testid="invoice-modal-close"><X size={20} weight="bold" /></button>
      </div>
    </div>
  );
}

function PaidSwitch({ paid, onToggle }) {
  return (
    <div className="mx-auto mb-3 flex max-w-[860px] items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs" data-testid="inv-paid-switch">
      <span className="text-slate-600">Status plaćanja (sinkronizirano s rezervacijom). Neplaćeni račun prikazuje podatke za plaćanje i HUB-3 barkod.</span>
      <div className="flex shrink-0 overflow-hidden rounded-md border border-slate-300 font-semibold">
        <button onClick={() => paid && onToggle(false)} className={`px-3 py-1 ${!paid ? "bg-rose-600 text-white" : "bg-white text-slate-600"}`} data-testid="inv-set-unpaid">Nije plaćeno</button>
        <button onClick={() => !paid && onToggle(true)} className={`px-3 py-1 ${paid ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="inv-set-paid">Plaćeno</button>
      </div>
    </div>
  );
}

function Banners({ inv, editable, itemsLocked }) {
  return (
    <div className="mx-auto mb-3 max-w-[860px] space-y-2">
      {inv.max_price_violation && (
        <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700" data-testid="inv-max-price-violation">
          <WarningCircle size={16} weight="fill" className="mt-px shrink-0" />
          <span>{inv.max_price_violation} Račun nije moguće zaključiti.</span>
        </div>
      )}
      {editable && itemsLocked && (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600" data-testid="inv-locked-note">
          <Lock size={13} weight="bold" /> Stavke, datum izdavanja i isporuke određeni su rezervacijom (dan odjave u 10:00) i ne mogu se mijenjati. Promijenite cijenu u rezervaciji pa kliknite „Osvježi”.
        </div>
      )}
    </div>
  );
}

function ModalFooter({ editable, isIssued, inv, saving, issuing, onSave, onIssue, onDelete, onPrint, onPDF, onTogglePaid, onStorno, onClose }) {
  const base = "rounded px-5 py-2 text-xs font-bold uppercase tracking-wide shadow-sm disabled:opacity-50";
  const blocked = !!inv.max_price_violation;
  // Compare in the tenant's own calendar day (Europe/Zagreb), matching the backend's issue check —
  // a plain UTC "today" could disagree with the backend around midnight.
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zagreb" }).format(new Date());
  const futureDated = inv.invoice_type !== "ponuda" && (inv.issue_date || "") > today;
  const del = <button onClick={onDelete} className={`${base} border border-rose-300 bg-white !px-3 text-rose-600 hover:bg-rose-50`} title="Obriši nacrt" data-testid="inv-btn-delete-draft"><Trash size={14} weight="bold" /></button>;
  const issue = (
    <button onClick={onIssue} disabled={issuing || saving || blocked || futureDated} className={`${base} bg-emerald-600 text-white hover:bg-emerald-700`}
      title={futureDated ? `Može se zaključiti tek ${fmtHrDate(inv.issue_date)} (datum u budućnosti)` : undefined} data-testid="inv-btn-zakljuci">
      {issuing ? <CircleNotch size={14} className="inline animate-spin" /> : "Zaključi"}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-3.5">
      {editable ? (
        <>
          <p className="max-w-lg text-[11px] italic text-slate-500">
            {futureDated
              ? <span className="font-semibold not-italic text-amber-600">Datum izdavanja je u budućnosti — zaključivanje je moguće tek {fmtHrDate(inv.issue_date)}</span>
              : "Zaključeni račun dobiva trajni redni broj i više se ne može mijenjati. „Pripremi” sprema nacrt koji možete kasnije urediti."}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onSave} disabled={saving || issuing} className={`${base} bg-amber-500 text-white hover:bg-amber-600`} data-testid="inv-btn-pripremi">{saving ? <CircleNotch size={14} className="inline animate-spin" /> : "Pripremi"}</button>
            {issue}{del}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <button onClick={onPrint} className={`${base} flex items-center gap-1.5 bg-[#0f3d4c] text-white hover:bg-[#164f62]`} data-testid="inv-btn-print"><Printer size={14} weight="bold" /> Ispis</button>
            <button onClick={onPDF} className={`${base} flex items-center gap-1.5 border border-[#0f3d4c] bg-white text-[#0f3d4c] hover:bg-slate-50`} data-testid="inv-btn-pdf"><FilePdf size={14} weight="bold" /> PDF</button>
            {isIssued && (
              <label className="ml-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <input type="checkbox" checked={!!inv.paid} onChange={onTogglePaid} className="h-4 w-4" data-testid="inv-toggle-paid" /> Naplaćeno (evidencija)
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={`${base} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`} data-testid="inv-btn-close">Zatvori</button>
            {!isIssued && (
              <>
                {futureDated && (
                  <span className="max-w-xs text-[11px] font-semibold text-amber-600" data-testid="inv-future-date-note">
                    Datum izdavanja je u budućnosti — zaključivanje je moguće tek {fmtHrDate(inv.issue_date)}
                  </span>
                )}
                {del}{issue}
              </>
            )}
            {isIssued && inv.invoice_type === "racun" && !inv.cancelled && <button onClick={onStorno} className={`${base} bg-rose-600 text-white hover:bg-rose-700`} data-testid="inv-btn-storno">Storniraj</button>}
          </div>
        </>
      )}
    </div>
  );
}

function ModalShell({ onClose, children }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }} data-testid="invoice-modal-backdrop">
      <div className="rw-light-scope relative flex max-h-[92dvh] w-[min(1000px,96vw)] flex-col overflow-hidden rounded-lg bg-white shadow-2xl" data-testid="invoice-modal">
        {children}
      </div>
    </div>,
    document.body,
  );
}
